import React, { useState } from 'react';
import { pluralize, ellipse } from 'library/helpers/text';
import * as StyledOutcome from 'apps/journey/pages/my-journey/components/styled-outcome';
import * as ModalsStyledOutcome from 'apps/journey/components/modals/outcomes/styled-outcome';
import ArrowSvg from 'library/icons/arrow.svg';
import { object, bool, func } from 'prop-types';
import CssDropdown from 'library/structures/css-dropdown';
import EllipsisSvg from 'library/icons/ellipsis-svg';
import ConfirmationModal from 'apps/journey/components/modals/confirmation-modal';
import {
  dismissRecommendedOutcome,
  dismissRecommendedOutcomeParseQuery,
} from 'apps/journey/queries/dismissOutcomeMutation';
import Notification, {
  Error as NotificationError,
  Success,
} from 'library/components/notification';
import { NavLink } from 'library/util/core-router';
import hasHoc from 'util/hoc/has-hoc';
import { resolve } from 'library/helpers/path-resolver';
import { Header5 } from 'library/components/text-elements';
import KeyCodes from 'library/helpers/key-codes';
import OutcomeDetails from 'apps/journey/pages/my-journey2/outcomes/outcome-details';
import JourneyTooltip from 'apps/journey/components/tooltip';

const Outcome = (props) => {
  const {
    outcome: {
      id,
      goals,
      goals_count,
      name,
      description,
      color,
      user_referrer,
      due_date,
    },
    active,
    handleClick,
    hideDetails,
    showEllipsis = false,
    modal,
    reload_graph,
    configuration,
    history,
  } = props;
  const [showModal, toggleModal] = useState(false);
  const outcomeLink = props.outcomeLink
    ? props.outcomeLink
    : `/admin/journey-map/outcome-view/${id}`;

  const isRecommendation = modal;
  const handleSubmit = async () => {
    const { data } = await dismissRecommendedOutcome({ outcomeId: id });
    const { success, errors } = dismissRecommendedOutcomeParseQuery(data);
    let NotificationComponent = Success;
    let message = 'The recommendation was successfully dismissed.';

    if (success) {
      reload_graph(false);
    } else {
      NotificationComponent = NotificationError;
      message = 'There was an error';
      console.log(errors);
    }
    Notification.new(
      { timeout: 5000 },
      <NotificationComponent>{message}</NotificationComponent>
    );
    if (success) toggleModal(false);
  };

  return (
    <ModalsStyledOutcome.Card
      className="recommendation"
      height={modal ? '9em' : '11.4375em'}
      width="100%"
      flex="45%"
      borderColor={color}
      active={active}
      role="document"
      onClick={() => history?.push(outcomeLink)}
    >
      <ConfirmationModal
        active={showModal}
        handleSubmit={handleSubmit}
        toggleActive={() => toggleModal(!showModal)}
        cancelText="No, cancel"
        confirmText={`Yes, I want to dismiss this ${resolve(
          configuration,
          'environment/journey/goals_name',
          'outcome'
        )}`}
      >
        <span>Are you sure you want to dismiss</span>
        <strong>{name}?</strong>
      </ConfirmationModal>
      <NavLink
        aria-label={`Select outcome ${props.outcome?.name}`}
        to={outcomeLink}
        tabIndex={0}
        className="arrowContainer"
      >
        <ArrowSvg className="arrow" />
      </NavLink>
      <ModalsStyledOutcome.HeaderRow>
        <StyledOutcome.Header>
          {name?.length > 50 ? (
            <JourneyTooltip
              activeOnHover
              tip={name}
              position={['center', 'top']}
              min_width={'200px'}
            >
              <Header5>{ellipse(name)}</Header5>
            </JourneyTooltip>
          ) : (
            <Header5>{ellipse(name)}</Header5>
          )}
          <span className="goalCount">
            {` ${goals_count || goals.length} ${pluralize(
              'goal',
              goals_count || goals.length
            )}`}
          </span>
        </StyledOutcome.Header>
        {showEllipsis && (
          <CssDropdown
            tabIndex={0}
            label={
              <EllipsisSvg
                width={25}
                fillColor={'#efefef'}
                className="ellipsis"
              />
            }
          >
            <StyledOutcome.OutcomeMenuItem
              className="menuItem"
              tabIndex={0}
              onClick={() => toggleModal(!showModal)}
              onKeyPress={(e) => {
                if (KeyCodes.POSITIVE_AFFIRMATIONS.includes(e.which)) {
                  toggleModal(!showModal);
                }
              }}
            >
              <StyledOutcome.RemoveSvg />{' '}
              <span>
                {isRecommendation
                  ? `Dismiss ${resolve(
                      configuration,
                      'environment/journey/goals_name',
                      'outcome'
                    )}`
                  : 'Dismiss Recommendation'}
              </span>
            </StyledOutcome.OutcomeMenuItem>
          </CssDropdown>
        )}
      </ModalsStyledOutcome.HeaderRow>
      <ModalsStyledOutcome.Body fontSize=".825em" height="9em">
        <p>{ellipse(description, modal ? 35 : 255)}</p>
        {!hideDetails && (
          <OutcomeDetails
            outcome={{ user_referrer, due_date }}
            height="unset"
          />
        )}
      </ModalsStyledOutcome.Body>
    </ModalsStyledOutcome.Card>
  );
};

Outcome.propTypes = {
  outcome: object,
  active: bool,
  handleClick: func,
  showEllipsis: bool,
  hideDetails: bool,
  modal: bool,
  reload_graph: func,
  configuration: object,
  history: object,
};
Outcome.hoc_list = ['withConfig', 'withRouter'];

export default hasHoc(Outcome);
